// frontend/src/components/AuthButton.js - UPDATED: Fetch min withdrawal from backend

import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import api from '../services/api';
import './AuthButton.css';
import WithdrawButton from './WithdrawButton';

// OTP confirmation UI — shared between STATE 2 and STATE 3 unconfirmed
const OtpConfirmUI = ({ user, otpCode, setOtpCode, otpMessage, otpLoading, showDropdown, setShowDropdown, handleVerifyOtp, handleResendOtp, handleLogout }) => (
  <div className="auth-button-container">
    <button
      className="auth-btn add-wallet-btn"
      style={{ backgroundColor: '#f39c12' }}
      onClick={() => setShowDropdown(!showDropdown)}
    >
      📧 Confirm Email
    </button>

    {showDropdown && (
      <div className="auth-dropdown" style={{ width: '300px' }}>
        <div className="dropdown-header">
          <h4>⚠️ Verify Your Email</h4>
          <p style={{ fontSize: '13px', color: '#666', margin: '5px 0 0 0' }}>{user.email}</p>
        </div>
        <div className="dropdown-content">
          <p style={{ fontSize: '13px', color: '#555', marginBottom: '12px' }}>
            Enter the 6-digit code sent to your email to activate your account.
          </p>
          <form onSubmit={handleVerifyOtp}>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              pattern="[0-9]{6}"
              placeholder="Enter 6-digit code"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '22px',
                letterSpacing: '8px',
                textAlign: 'center',
                border: '2px solid #667eea',
                borderRadius: '8px',
                marginBottom: '10px',
                fontFamily: 'monospace',
                boxSizing: 'border-box'
              }}
              disabled={otpLoading}
              autoFocus
            />
            {otpMessage.text && (
              <p style={{
                fontSize: '13px',
                color: otpMessage.type === 'success' ? '#28a745' : '#dc3545',
                margin: '0 0 10px 0',
                textAlign: 'center'
              }}>{otpMessage.text}</p>
            )}
            <button
              type="submit"
              disabled={otpLoading || otpCode.length !== 6}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#667eea',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: otpCode.length === 6 ? 'pointer' : 'not-allowed',
                marginBottom: '8px'
              }}
            >
              {otpLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          </form>
          <button
            onClick={handleResendOtp}
            disabled={otpLoading}
            style={{
              width: '100%',
              padding: '8px',
              backgroundColor: 'transparent',
              color: '#667eea',
              border: '1px solid #667eea',
              borderRadius: '6px',
              fontSize: '13px',
              cursor: 'pointer'
            }}
          >
            {otpLoading ? 'Sending...' : 'Resend Code'}
          </button>
        </div>
        <div className="dropdown-actions">
          <button className="dropdown-btn" onClick={handleLogout}>Logout</button>
        </div>
      </div>
    )}
  </div>
);

const AuthButton = () => {
  const [user, setUser] = useState(null);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [walletStatus, setWalletStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(true);
  const [otpCode, setOtpCode] = useState('');
  const [otpMessage, setOtpMessage] = useState({ text: '', type: '' });
  const [otpLoading, setOtpLoading] = useState(false);

  // ✅ NEW: State for withdrawal config from backend
  const [minWithdrawal, setMinWithdrawal] = useState(10.0); // Default fallback

  useEffect(() => {
    checkAuthStatus();
    fetchWithdrawalConfig(); // ✅ Fetch config on mount

    // Listen for balance updates from article reads
    const handleBalanceUpdate = (event) => {
      console.log('Balance update event received:', event.detail);
      if (event.detail && event.detail.balance !== undefined) {
        setBalance(prevBalance => {
          const newBalance = {
            ...prevBalance,
            balance_doge: event.detail.balance,
            articles_read_count: (prevBalance?.articles_read_count || 0) + 1
          };
          console.log('Updating balance:', prevBalance?.balance_doge, '→', newBalance.balance_doge);
          return newBalance;
        });
      }
    };

    window.addEventListener('balanceUpdate', handleBalanceUpdate);

    return () => {
      window.removeEventListener('balanceUpdate', handleBalanceUpdate);
    };
  }, []);

  // Fetch withdrawal config using api.js (correct stage URL + auth)
  const fetchWithdrawalConfig = async () => {
    try {
      const response = await api.get('/withdraw/config');
      setMinWithdrawal(response.data.min_withdrawal_amount);
      console.log('✅ Withdrawal config loaded:', response.data);
    } catch (error) {
      console.error('⚠️ Failed to fetch withdrawal config:', error);
      // Keep default fallback (10.0)
    }
  };


  const checkAuthStatus = async () => {
    if (!authService.isAuthenticated()) {
      setBalanceLoading(false);
      return;
    }

    try {
      // Get user data (synchronous)
      const userData = authService.getCurrentUser();

      console.log('👤 AuthButton - User data:', userData);

      if (!userData) {
        console.log('❌ No user data found');
        setBalanceLoading(false);
        return;
      }

      setUser(userData);

      // Check newsletter subscription status
      try {
        const newsletterStatus = await authService.getNewsletterStatus();
        setNewsletterSubscribed(newsletterStatus.newsletter_subscribed);
      } catch (error) {
        console.log('Could not load newsletter status:', error);
      }

      // Get balance for all confirmed users — wallet not required to earn rewards
      if (userData.is_confirmed) {
        try {
          setBalanceLoading(true);
          const balanceData = await authService.getBalance();
          console.log('💰 Balance loaded:', balanceData);
          setBalance(balanceData);
        } catch (error) {
          console.error('❌ Could not load balance:', error);
          setBalance({ balance_doge: 0, total_earned: 0, articles_read_count: 0 });
        } finally {
          setBalanceLoading(false);
        }
        // Also fetch wallet status for optional wallet add CTA
        try {
          const status = await authService.getWalletStatus();
          setWalletStatus(status);
        } catch (error) {
          console.log('Could not load wallet status:', error);
        }
      } else {
        setBalanceLoading(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setBalanceLoading(false);
    }
  };

  const refreshUserData = async () => {
    setLoading(true);
    try {
      console.log('🔄 Refreshing user data from database...');

      // Fetch fresh user data directly from API
      const freshUserData = await authService.fetchUserFromAPI();

      console.log('✅ Fresh user data received:', freshUserData);

      // Update local state with fresh data
      setUser(freshUserData);

      // Load balance for all confirmed users (wallet not required to earn)
      if (freshUserData.is_confirmed) {
        try {
          setBalanceLoading(true);
          const balanceData = await authService.getBalance();
          console.log('💰 Balance loaded after refresh:', balanceData);
          setBalance(balanceData);
        } catch (balError) {
          console.error('❌ Could not load balance:', balError);
          // Set default balance if fetch fails
          setBalance({
            balance_doge: 0,
            total_earned: 0,
            articles_read_count: 0
          });
        } finally {
          setBalanceLoading(false);
        }
      } else {
        setBalanceLoading(false);
      }

      // Keep dropdown open
      setShowDropdown(true);

      // Show success message based on what changed
      if (freshUserData.is_confirmed) {
        alert('✅ Email confirmed! Your loyalty rewards are now active. Start reading articles to earn rewards!');
      } else {
        alert('✅ User data refreshed!');
      }
    } catch (error) {
      console.error('❌ Failed to refresh data:', error);
      alert('❌ Failed to refresh data. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddWallet = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const response = await authService.addWallet(walletAddress);

      let successMessage = '✅ Wallet added! Start earning rewards! 🎉';
      if (response.bonus_earned && response.bonus_earned > 0) {
        successMessage = `✅ Wallet added! Welcome bonus credited! 🎁`;
      }

      setMessage({
        text: successMessage,
        type: 'success'
      });

      // Keep dropdown open
      setShowDropdown(true);

      // Wait 2 seconds, then refresh data instead of page reload
      setTimeout(async () => {
        setShowWalletModal(false);
        console.log('🔄 Refreshing after wallet add...');

        // Refresh user data from backend
        await refreshUserData();

        // If that doesn't work, reload page
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }, 2000);
    } catch (error) {
      setMessage({ text: `❌ ${error}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    authService.logout();
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      '⚠️ Delete Account\n\nAre you sure you want to permanently delete your account?\n\nAll accumulated loyalty rewards (DOGE balance) will be forfeited and cannot be recovered.\n\nThis action is irreversible.'
    );
    if (!confirmed) return;
    try {
      await authService.deleteAccount();
      window.location.href = '/';
    } catch (err) {
      alert('❌ ' + (err || 'Failed to delete account. Please try again.'));
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      setOtpMessage({ text: 'Enter the 6-digit code from your email', type: 'error' });
      return;
    }
    setOtpLoading(true);
    setOtpMessage({ text: '', type: '' });
    try {
      await authService.verifyCode(user.email, otpCode);
      setOtpMessage({ text: 'Email verified! Loading your account...', type: 'success' });
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      setOtpMessage({ text: String(err), type: 'error' });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpMessage({ text: '', type: '' });
    try {
      await authService.resendCode(user.email);
      setOtpMessage({ text: 'New code sent! Check your inbox.', type: 'success' });
    } catch (err) {
      setOtpMessage({ text: String(err), type: 'error' });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResubscribe = async () => {
    setLoading(true);
    try {
      const result = await authService.resubscribeNewsletter();
      setNewsletterSubscribed(true);
      alert('✅ ' + result.message);
    } catch (error) {
      alert('❌ Failed to resubscribe: ' + error);
    } finally {
      setLoading(false);
    }
  };

  // Debug log
  console.log('🔍 AuthButton render - user:', user, 'balance:', balance, 'loading:', balanceLoading, 'minWithdrawal:', minWithdrawal);

  // If not authenticated, don't show anything
  if (!user) {
    return null;
  }

  // STATE 1: User is confirmed - Show rewards (wallet optional, needed only for withdrawal)
  if (user.is_confirmed) {
    const displayBalance = balanceLoading ? '...' : (balance?.balance_doge || 0).toFixed(2);
    // ✅ UPDATED: Use minWithdrawal from backend instead of hardcoded value
    const currentBalance = balance?.balance_doge || 0;
    const canWithdraw = currentBalance >= minWithdrawal;

    return (
      <div className="auth-button-container">
        <button
          className="auth-btn rewards-btn"
          onClick={() => setShowDropdown(!showDropdown)}
        >
          🎉 Rewards: {displayBalance} pts
        </button>

        {showDropdown && (
          <div className="auth-dropdown">
            <div className="dropdown-header">
              <h4>Your Rewards 💰</h4>
              <p style={{
                fontSize: '13px',
                color: '#666',
                margin: '5px 0 0 0',
                fontWeight: 'normal'
              }}>
                {user.email}
              </p>
            </div>
            <div className="dropdown-content">
              {balanceLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  Loading balance...
                </div>
              ) : (
                <>
                  <div className="stat-row">
                    <span>Balance:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <strong>{(balance?.balance_doge || 0).toFixed(2)} pts</strong>
                      {canWithdraw && (
                        <WithdrawButton
                          onWithdrawSuccess={() => { checkAuthStatus(); }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="stat-row">
                    <span>Total Rewarded:</span>
                    <strong>{(balance?.total_earned || 0).toFixed(2)} pts</strong>
                  </div>

                  <div className="stat-row">
                    <span>Articles Read:</span>
                    <strong>{balance?.articles_read_count || 0}</strong>
                  </div>

                  <div className="stat-row">
                    <span>Wallet:</span>
                    {user.dogecoin_wallet ? (
                      <strong style={{ fontSize: '11px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {user.dogecoin_wallet.substring(0, 8)}...
                      </strong>
                    ) : (
                      <button
                        onClick={() => { setShowWalletModal(true); setShowDropdown(false); }}
                        style={{ fontSize: '12px', color: '#667eea', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: '600' }}
                      >
                        + Add wallet to withdraw
                      </button>
                    )}
                  </div>

                  {/* ✅ UPDATED: Show dynamic min withdrawal amount */}
                  {!canWithdraw && currentBalance > 0 && (
                    <p style={{
                      fontSize: '12px',
                      color: '#999',
                      marginTop: '10px',
                      padding: '8px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '4px',
                      textAlign: 'center'
                    }}>
                      💡 Need {(minWithdrawal - currentBalance).toFixed(2)} more pts to redeem (min: {minWithdrawal} pts)
                    </p>
                  )}

                  {!newsletterSubscribed && (
                    <div style={{
                      marginTop: '15px',
                      padding: '10px',
                      backgroundColor: '#fff3cd',
                      border: '1px solid #ffc107',
                      borderRadius: '6px',
                      fontSize: '13px'
                    }}>
                      <p style={{ margin: '0 0 8px 0', fontWeight: 'bold', color: '#856404' }}>
                        📧 Get More News!
                      </p>
                      <p style={{ margin: '0 0 10px 0', color: '#856404' }}>
                        Subscribe to our newsletter for daily curated articles
                      </p>
                      <button
                        onClick={handleResubscribe}
                        disabled={loading}
                        style={{
                          backgroundColor: '#ffc107',
                          color: '#000',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 'bold'
                        }}
                      >
                        {loading ? 'Subscribing...' : 'Subscribe Now'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            {/* Add Wallet Modal */}
            {showWalletModal && (
              <div style={{
                position:'fixed',top:0,left:0,right:0,bottom:0,
                backgroundColor:'rgba(0,0,0,0.5)',zIndex:9999,
                display:'flex',alignItems:'center',justifyContent:'center'
              }} onClick={() => setShowWalletModal(false)}>
                <div style={{
                  background:'#fff',borderRadius:'12px',padding:'24px',
                  width:'340px',maxWidth:'90vw',boxShadow:'0 8px 32px rgba(0,0,0,0.2)'
                }} onClick={e => e.stopPropagation()}>
                  <h3 style={{margin:'0 0 8px',fontSize:'16px'}}>Add Redemption Wallet</h3>
                  <p style={{margin:'0 0 16px',fontSize:'13px',color:'#666'}}>
                    Enter your Dogecoin wallet address to enable reward redemptions.
                  </p>
                  <input
                    type="text"
                    placeholder="Dogecoin address (D..., 34 chars)"
                    value={walletAddress}
                    onChange={e => setWalletAddress(e.target.value)}
                    maxLength={34}
                    style={{
                      width:'100%',padding:'10px 12px',borderRadius:'8px',
                      border:'1px solid #ddd',fontSize:'13px',
                      boxSizing:'border-box',marginBottom:'8px'
                    }}
                  />
                  {message.text && (
                    <p style={{fontSize:'13px',color:message.type==='success'?'#2e7d32':'#c62828',margin:'0 0 8px'}}>
                      {message.text}
                    </p>
                  )}
                  <div style={{display:'flex',gap:'8px',marginTop:'8px'}}>
                    <button onClick={() => { setShowWalletModal(false); setMessage({text:'',type:''}); }}
                      style={{flex:1,padding:'10px',borderRadius:'8px',border:'1px solid #ddd',background:'#f5f5f5',cursor:'pointer',fontSize:'14px'}}>
                      Cancel
                    </button>
                    <button onClick={handleAddWallet} disabled={loading}
                      style={{flex:1,padding:'10px',borderRadius:'8px',border:'none',background:'#667eea',color:'#fff',cursor:'pointer',fontSize:'14px',fontWeight:'600'}}>
                      {loading ? 'Saving...' : 'Save Wallet'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="dropdown-actions">
              <button className="dropdown-btn" onClick={handleLogout}>
                Logout
              </button>
              <button
                className="dropdown-btn"
                onClick={handleDeleteAccount}
                style={{color:'#e74c3c',fontSize:'12px',opacity:0.7,marginTop:'4px'}}
              >
                Delete Account
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // STATE 2: Unconfirmed email - Show OTP form
  if (!user.is_confirmed) {
    return <OtpConfirmUI user={user} otpCode={otpCode} setOtpCode={setOtpCode} otpMessage={otpMessage} otpLoading={otpLoading} showDropdown={showDropdown} setShowDropdown={setShowDropdown} handleVerifyOtp={handleVerifyOtp} handleResendOtp={handleResendOtp} handleLogout={handleLogout} />;
  }


  return null;
};

export default AuthButton;
